import { z } from "zod";

import { CompanyOrgStructureDocument } from "./orgStructureTypes";

export const MAX_DESCRIPTION_LENGTH = 2000;
export const MAX_TITLE_LENGTH = 80;
export const MAX_NAME_LENGTH = 80;
export const MAX_ROLE_LABEL_LENGTH = 80;
export const MAX_NODES = 100;
export const MAX_ROLES = 100;

const descriptionSchema = z.string().max(MAX_DESCRIPTION_LENGTH);
const titleSchema = z.string().max(MAX_TITLE_LENGTH);
const nameSchema = z.string().max(MAX_NAME_LENGTH);
const roleLabelSchema = z.string().max(MAX_ROLE_LABEL_LENGTH);

export const orgNodeKindSchema = z.enum([
    "leadership",
    "division",
    "unit",
    "team",
    "key_persons"
]);

export const orgRoleSchema = z.object({
    id: z.string().min(1),
    label: roleLabelSchema
});

export const orgPersonSchema = z.object({
    id: z.string().min(1),
    name: nameSchema,
    role_id: z.string().min(1)
});

export const orgNodeSchema = z.object({
    id: z.string().min(1),
    kind: orgNodeKindSchema,
    parent_id: z.string().nullable(),
    title: titleSchema,
    description: descriptionSchema,
    people: z.array(orgPersonSchema)
});

export const companyOrgStructureDocumentSchema = z.object({
    schema_version: z.number().int(),
    roles: z.array(orgRoleSchema).max(MAX_ROLES),
    nodes: z.array(orgNodeSchema).max(MAX_NODES),
    updated_at: z.number().int().optional(),
    updated_by: z.string().optional()
});

export function clampDescription(value: string): string {
    return value.slice(0, MAX_DESCRIPTION_LENGTH);
}

export function clampTitle(value: string): string {
    return value.slice(0, MAX_TITLE_LENGTH);
}

export function clampName(value: string): string {
    return value.slice(0, MAX_NAME_LENGTH);
}

export function clampRoleLabel(value: string): string {
    return value.slice(0, MAX_ROLE_LABEL_LENGTH);
}

function collectDescendantIds(nodes: CompanyOrgStructureDocument["nodes"], rootId: string): Set<string> {
    const descendants = new Set<string>();
    const queue = [rootId];
    while (queue.length > 0) {
        const current = queue.shift()!;
        for (const node of nodes) {
            if (node.parent_id === current && !descendants.has(node.id)) {
                descendants.add(node.id);
                queue.push(node.id);
            }
        }
    }
    return descendants;
}

function hasCycle(nodes: CompanyOrgStructureDocument["nodes"]): boolean {
    const nodeIds = new Set(nodes.map(node => node.id));
    for (const node of nodes) {
        const visited = new Set<string>();
        let current: string | null = node.id;
        while (current !== null) {
            if (visited.has(current)) {
                return true;
            }
            visited.add(current);
            const parent = nodes.find(item => item.id === current);
            if (!parent) {
                break;
            }
            const parentId = parent.parent_id;
            if (parentId !== null && !nodeIds.has(parentId)) {
                return true;
            }
            current = parentId;
        }
    }
    return false;
}

export function validateDocument(document: unknown): string | null {
    const parsed = companyOrgStructureDocumentSchema.safeParse(document);
    if (!parsed.success) {
        return parsed.error.issues[0]?.message ?? "Invalid org structure document";
    }

    const data = parsed.data;
    const seen = new Set<string>();
    const roleIds = new Set(data.roles.map(role => role.id));
    const nodeIds = new Set(data.nodes.map(node => node.id));

    for (const role of data.roles) {
        if (seen.has(role.id)) {
            return `Duplicate role id: ${role.id}`;
        }
        seen.add(role.id);
    }

    for (const node of data.nodes) {
        if (seen.has(node.id)) {
            return `Duplicate node id: ${node.id}`;
        }
        seen.add(node.id);

        for (const person of node.people) {
            if (seen.has(person.id)) {
                return `Duplicate person id: ${person.id}`;
            }
            seen.add(person.id);
            if (!roleIds.has(person.role_id)) {
                return `Person ${person.id} references unknown role: ${person.role_id}`;
            }
        }

        if (node.parent_id !== null) {
            if (!nodeIds.has(node.parent_id)) {
                return `Node ${node.id} references unknown parent: ${node.parent_id}`;
            }
            if (node.parent_id === node.id) {
                return `Node ${node.id} cannot be its own parent`;
            }
            const descendants = collectDescendantIds(data.nodes, node.id);
            if (descendants.has(node.parent_id)) {
                return `Cycle detected involving node ${node.id}`;
            }
        }
    }

    if (hasCycle(data.nodes)) {
        return "Cycle detected in org structure";
    }

    return null;
}
